import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProject } from '../../contexts/ProjectContext';
import type { SprintEpic, IssueExecution } from '../../api/sprint';
import { fetchSprintIssueExecutions, execStatusToWaterfall } from '../../api/sprint';
import { flatIssuesApi } from '../../api/work';
import type { TelemetryEvent } from '../../api/telemetry';
import { useSpriteActivity } from '../../api/telemetry';
import { SprintWaterfall, type WaterfallIssue, type PhaseEvent } from '../workflow/SprintWaterfall';
import { IssueSlideOut } from '../workflow/IssueSlideOut';
import WorkflowWaterfall from '../telemetry/WorkflowWaterfall';
import UnifiedEventTable from '../telemetry/UnifiedEventTable';
import WorkflowHierarchyTree from '../telemetry/WorkflowHierarchyTree';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface Props {
  sprint: any;
  epics: SprintEpic[];
  issueExecutions: IssueExecution[];
  telemetryEvents: TelemetryEvent[];
  timelineData: any;
  childTree: any;
  batchWorkflowId?: string;
}

export function MonitorPlanTab({ sprint, epics, issueExecutions, telemetryEvents, timelineData, childTree, batchWorkflowId }: Props) {
  const { project } = useProject();
  const [slideOutIssue, setSlideOutIssue] = useState<WaterfallIssue | null>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | undefined>(undefined);

  // Waterfall execution data
  const { data: waterfallExecutions } = useQuery({
    queryKey: ['sprint-issue-executions-waterfall', sprint?.batchId],
    queryFn: () => fetchSprintIssueExecutions(sprint!.batchId!),
    enabled: !!sprint?.batchId,
    refetchInterval: sprint?.status === 'building' ? 5000 : false,
    staleTime: 3_000,
  });

  // Epic titles
  const { data: allProjectIssues } = useQuery({
    queryKey: ['project-issues-flat', project?.id],
    queryFn: () => flatIssuesApi.list(project!.id),
    enabled: !!project?.id,
    staleTime: 30_000,
  });

  const epicTitleMap = useMemo(() => {
    const titleMap = new Map<string, string>();
    const issues = allProjectIssues?.issues || allProjectIssues || [];
    if (!Array.isArray(issues)) return titleMap;
    for (const iss of issues) {
      if (iss.type === 'epic') titleMap.set(iss.id, iss.title);
    }
    return titleMap;
  }, [allProjectIssues]);

  const waterfallIssues = useMemo<WaterfallIssue[]>(() => {
    const executions = waterfallExecutions || issueExecutions || [];
    const sprintStart = sprint?.startedAt || sprint?.createdAt;
    if (!sprintStart || executions.length === 0) return [];
    const sprintStartMs = new Date(sprintStart).getTime();

    return executions.map((exec: IssueExecution) => {
      const startMs = exec.startedAt ? new Date(exec.startedAt).getTime() : sprintStartMs;
      const endMs = exec.completedAt ? new Date(exec.completedAt).getTime() : Date.now();
      const duration = exec.durationMs || (endMs - startMs) || 60_000;

      return {
        id: exec.issueId,
        label: exec.issueTitle || exec.issueId,
        epicId: exec.epicId || '',
        status: execStatusToWaterfall(exec.status),
        startOffset: Math.max(0, startMs - sprintStartMs),
        duration,
      };
    });
  }, [waterfallExecutions, issueExecutions, sprint]);

  // Phase events for sub-bars
  const spriteActivityForPhases = useSpriteActivity(sprint?.batchId || '', {
    enabled: !!sprint?.batchId,
    limit: 500,
  });

  const waterfallPhaseEvents = useMemo<PhaseEvent[]>(() => {
    const events = spriteActivityForPhases.data?.events || [];
    return events
      .filter((e: any) => e.event_type === 'sprite_phase' || e.event_type === 'sprite_phase_changed')
      .map((e: any) => ({
        issueId: e.issue_id || e.story_id || '',
        phase: e.phase || '',
        timestamp: e.timestamp,
      }))
      .filter((e: PhaseEvent) => e.issueId && e.phase);
  }, [spriteActivityForPhases.data?.events]);

  // Slide-out sync
  useEffect(() => {
    if (slideOutIssue) {
      const updated = waterfallIssues.find(i => i.id === slideOutIssue.id);
      if (updated && (updated.status !== slideOutIssue.status || updated.duration !== slideOutIssue.duration)) {
        setSlideOutIssue(updated);
      }
    }
  }, [waterfallIssues, slideOutIssue]);

  const slideOutEpic = slideOutIssue ? epics.find(e => e.epicId === slideOutIssue.epicId) : undefined;
  const historyWorkflowId = selectedWorkflowId || batchWorkflowId;

  // Filtered telemetry for selected story
  const selectedStoryEvents = useMemo(() => {
    if (!slideOutIssue) return [];
    return telemetryEvents.filter(e =>
      e.issueId === slideOutIssue.id ||
      (e.payload as any)?.issueId === slideOutIssue.id ||
      (e.payload as any)?.storyId === slideOutIssue.id
    );
  }, [telemetryEvents, slideOutIssue]);

  return (
    <div className="space-y-5">
      {/* Loading state */}
      {sprint && epics.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="w-5 h-5 border-2 border-neutral-300 border-t-coral-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Gantt waterfall */}
      {sprint && epics.length > 0 && (
        <SprintWaterfall
          epics={epics}
          issues={waterfallIssues}
          sprintStartTime={sprint.startedAt || sprint.createdAt}
          endTime={sprint.completedAt || sprint.failedAt}
          selectedIssueId={slideOutIssue?.id}
          onSelectIssue={setSlideOutIssue}
          staticMode={sprint.status !== 'building'}
          epicTitleMap={epicTitleMap}
          phaseEvents={waterfallPhaseEvents}
        />
      )}

      {/* Selected story event table */}
      {slideOutIssue && selectedStoryEvents.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-3">
            Events for {slideOutIssue.label}
          </p>
          <UnifiedEventTable events={selectedStoryEvents} />
        </div>
      )}

      {/* Sprint plan tree */}
      {sprint?.id && epics.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-3">Sprint Plan</p>
          <div className="space-y-2">
            {epics.map(epic => {
              const epicIssues = waterfallIssues.filter(i => i.epicId === epic.epicId);
              const done = epicIssues.filter(i => i.status === 'completed').length;
              return (
                <div key={epic.epicId} className="border border-neutral-100 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-neutral-50/80 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      epic.status === 'completed' ? 'bg-accent-500' :
                      epic.status === 'building' ? 'bg-coral-500 animate-pulse' :
                      epic.status === 'failed' ? 'bg-red-500' : 'bg-neutral-300'
                    }`} />
                    <span className="text-[11px] font-bold text-neutral-700 truncate">
                      {epicTitleMap.get(epic.epicId) || `Epic ${epic.order}`}
                    </span>
                    <span className="text-[9px] text-neutral-400 font-mono ml-auto">{done}/{epicIssues.length}</span>
                  </div>
                  {epicIssues.length > 0 && (
                    <div className="divide-y divide-neutral-50">
                      {epicIssues.map(iss => (
                        <div key={iss.id} className="px-3 py-1.5 flex items-center gap-2 text-[11px]">
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            iss.status === 'completed' ? 'bg-accent-500' :
                            iss.status === 'building' ? 'bg-coral-500' :
                            iss.status === 'failed' ? 'bg-red-500' : 'bg-neutral-300'
                          }`} />
                          <span className="font-mono text-neutral-500 w-16 shrink-0">{iss.id}</span>
                          <span className="text-neutral-600 truncate">{iss.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Collapsible workflow details */}
      {batchWorkflowId && (
        <details className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
          <summary className="px-4 py-3 cursor-pointer text-[11px] font-bold uppercase tracking-widest text-neutral-500 hover:bg-neutral-50 transition-colors select-none">
            Workflow Details (Temporal)
          </summary>
          <div className="px-4 pb-4 space-y-4 border-t border-neutral-100 pt-4">
            {childTree && (
              <WorkflowHierarchyTree
                tree={childTree}
                selectedWorkflowId={historyWorkflowId}
                onSelect={setSelectedWorkflowId}
              />
            )}
            {timelineData?.activityLifecycles && (
              <WorkflowWaterfall
                activityLifecycles={timelineData.activityLifecycles}
                customEvents={timelineData.events}
              />
            )}
            {timelineData?.events && (
              <UnifiedEventTable events={timelineData.events} />
            )}
          </div>
        </details>
      )}

      {/* Issue slide-out */}
      {slideOutIssue && (
        <IssueSlideOut
          issue={slideOutIssue}
          epic={slideOutEpic}
          telemetryEvents={telemetryEvents}
          onClose={() => setSlideOutIssue(null)}
        />
      )}
    </div>
  );
}
